import dayjs from 'dayjs';
import { BaseCriteria, CompositeCriteria, EligibilityCriteria } from './types';

type StrategyResult = {
  shouldShow: boolean;
  updatedCriteria?: EligibilityCriteria;
};

// Type guard to check if criteria is composite
const isCompositeCriteria = (criteria: EligibilityCriteria): criteria is CompositeCriteria => {
  return (
    'strategy' in criteria && (criteria.strategy === 'any' || criteria.strategy === 'all') && 'conditions' in criteria
  );
};

// Type guard to check if criteria is base
const isBaseCriteria = (criteria: EligibilityCriteria): criteria is BaseCriteria => {
  return 'type' in criteria && !('strategy' in criteria);
};

// Base strategy evaluators
// Return shouldShow: true only if the limit has NOT been reached

/**
 * Evaluates 'days-shown' criteria:
 * - Shows the feature until maxDaysToShow is exceeded.
 * - Increments daysShown only once per day based on lastSeenDate.
 *
 * @param criteria
 * @returns
 */
const daysShownStrategy = (criteria: BaseCriteria & { type: 'days-shown' }): StrategyResult => {
  const today = new Date().toISOString().split('T')[0];
  const { lastSeenDate, daysShown = 0, maxDaysToShow } = criteria;

  // Check if limit already exceeded
  if (daysShown > maxDaysToShow) {
    return { shouldShow: false };
  }

  // First time or new day - increment
  if (!lastSeenDate || lastSeenDate !== today) {
    const newDaysShown = lastSeenDate ? daysShown + 1 : 1;
    return {
      // Show only if we haven't exceeded the limit after incrementing
      shouldShow: newDaysShown <= maxDaysToShow,
      updatedCriteria: {
        ...criteria,
        lastSeenDate: today,
        daysShown: newDaysShown,
      },
    };
  }

  // Same day, check current count
  return { shouldShow: daysShown <= maxDaysToShow };
};

/**
 * Evaluates 'click-count' criteria:
 * - Shows the feature until maxClicks is exceeded.
 * - DOES NOT increment clickCount automatically; increments only when user clicks.
 *
 * @param criteria
 * @returns
 */
const clickCountStrategy = (criteria: BaseCriteria & { type: 'click-count' }): StrategyResult => {
  const { clickCount = 0, maxClicks } = criteria;
  // Show only if clicks are BELOW the max (not exceeded)
  return { shouldShow: clickCount < maxClicks };
};

/**
 * Evaluates 'view-count' criteria:
 * - Shows the feature until maxViews is exceeded.
 * - Increments viewCount on each view.
 *
 * @param criteria
 * @returns
 */
const viewCountStrategy = (criteria: BaseCriteria & { type: 'view-count' }): StrategyResult => {
  const { viewCount = 0, maxViews } = criteria;

  // Check if limit already exceeded
  if (viewCount >= maxViews) {
    return { shouldShow: false };
  }

  const newViewCount = viewCount + 1;
  return {
    // Show only if we haven't exceeded the limit after incrementing
    shouldShow: newViewCount <= maxViews,
    updatedCriteria: {
      ...criteria,
      viewCount: newViewCount,
    },
  };
};

/**
 * Evaluates 'date-range' criteria:
 * - Shows the feature only within the specified date range.
 * - Does not require any updates to criteria since it's based on fixed dates.
 *
 * @param criteria
 * @returns
 */
const dateRangeStrategy = (criteria: BaseCriteria & { type: 'date-range' }): StrategyResult => {
  const today = new Date().toISOString().split('T')[0];
  const { startDate, endDate } = criteria;
  // Show only if we're within the date range
  return { shouldShow: today >= startDate && today <= endDate };
};

/**
 * Evaluates 'dismiss-cooldown' criteria:
 * - Hides permanently after maxDismisses is reached.
 * - Hides temporarily for cooldownDays after each dismiss.
 * - DOES NOT increment automatically; increments only via recordDismiss().
 *
 * @param criteria
 * @returns
 */
const dismissCooldownStrategy = (criteria: BaseCriteria & { type: 'dismiss-cooldown' }): StrategyResult => {
  const { dismissCount = 0, maxDismisses, cooldownDays, lastDismissDate } = criteria;

  // Permanently hidden after reaching max dismisses
  if (dismissCount >= maxDismisses) {
    return { shouldShow: false };
  }

  // Within cooldown period after a dismiss
  if (lastDismissDate) {
    const diffDays = dayjs().diff(dayjs(lastDismissDate), 'day'); // Check if last dismiss was today
    if (diffDays < cooldownDays) {
      return { shouldShow: false };
    }
  }

  return { shouldShow: true };
};

const dismissCooldownSequenceStrategy = (
  criteria: BaseCriteria & { type: 'dismiss-cooldown-sequence' }
): StrategyResult => {
  const { dismissCount = 0, cooldownDays, lastDismissDate } = criteria;

  if (!lastDismissDate) {
    return { shouldShow: true };
  }

  const activeCooldown = cooldownDays[Math.min(dismissCount - 1, cooldownDays.length - 1)];
  const diffDays = dayjs().diff(dayjs(lastDismissDate), 'day');

  if (diffDays < activeCooldown) {
    return { shouldShow: false };
  }

  return { shouldShow: true };
};

const evaluateBaseCriteria = (criteria: BaseCriteria): StrategyResult => {
  switch (criteria.type) {
    case 'days-shown':
      return daysShownStrategy(criteria);
    case 'click-count':
      return clickCountStrategy(criteria);
    case 'view-count':
      return viewCountStrategy(criteria);
    case 'date-range':
      return dateRangeStrategy(criteria);
    case 'dismiss-cooldown':
      return dismissCooldownStrategy(criteria);
    case 'dismiss-cooldown-sequence':
      return dismissCooldownSequenceStrategy(criteria);
    default:
      return { shouldShow: false };
  }
};

/**
 * Composite criteria logic:
 *
 * strategy: 'any' (OR to HIDE): Hide when ANY condition's limit is reached.
 *   - Use case: "Stop showing after 3 days OR 5 clicks, whichever comes first"
 *   - shouldShow = ALL conditions must still be valid (none exceeded)
 *
 * strategy: 'all' (AND to HIDE): Hide only when ALL conditions' limits are reached.
 *   - Use case: "Stop showing only after BOTH 3 days AND 5 clicks have been reached"
 *   - shouldShow = AT LEAST ONE condition must still be valid
 */
const evaluateCompositeCriteria = (criteria: CompositeCriteria): StrategyResult => {
  // Safety check for conditions array
  if (!criteria.conditions || !Array.isArray(criteria.conditions)) {
    return { shouldShow: false };
  }

  const results = criteria.conditions.map((condition) => ({
    condition,
    result: evaluateStrategy(condition),
  }));

  const updatedConditions = results.map(({ condition, result }) => result.updatedCriteria ?? condition);
  const hasUpdates = results.some(({ result }) => result.updatedCriteria);

  let shouldShow: boolean;

  if (criteria.strategy === 'any') {
    // 'any' means "hide when ANY limit is reached"
    // So we show only if ALL conditions are still valid (no limit reached)
    shouldShow = results.every(({ result }) => result.shouldShow);
  } else {
    // 'all' means "hide when ALL limits are reached"
    // So we show if AT LEAST ONE condition is still valid
    shouldShow = results.some(({ result }) => result.shouldShow);
  }

  return {
    shouldShow,
    updatedCriteria: hasUpdates ? { ...criteria, conditions: updatedConditions } : undefined,
  };
};

export const evaluateStrategy = (criteria: EligibilityCriteria): StrategyResult => {
  // Use type guard instead of just checking type string
  if (isCompositeCriteria(criteria)) {
    return evaluateCompositeCriteria(criteria);
  }

  if (isBaseCriteria(criteria)) {
    return evaluateBaseCriteria(criteria);
  }

  // Fallback for malformed criteria
  return { shouldShow: false };
};

/**
 * Recursively traverses criteria to find and increment click-count conditions.
 *
 * @param criteria
 * @returns
 */
export const incrementClickCount = (criteria: EligibilityCriteria): EligibilityCriteria | undefined => {
  if ('type' in criteria && criteria.type === 'click-count') {
    return {
      ...criteria,
      clickCount: (criteria.clickCount ?? 0) + 1,
    };
  }

  if (isCompositeCriteria(criteria)) {
    let hasUpdate = false;
    const updatedConditions = criteria.conditions.map((condition) => {
      const updated = incrementClickCount(condition);
      if (updated) {
        hasUpdate = true;
        return updated;
      }
      return condition;
    });
    return hasUpdate ? { ...criteria, conditions: updatedConditions } : undefined;
  }

  return undefined;
};

/**
 * Recursively traverses criteria to find and increment dismiss-cooldown conditions.
 *
 * @param criteria
 * @returns
 */
export const incrementDismissCount = (criteria: EligibilityCriteria): EligibilityCriteria | undefined => {
  if ('type' in criteria && (criteria.type === 'dismiss-cooldown' || criteria.type === 'dismiss-cooldown-sequence')) {
    const today = new Date().toISOString().split('T')[0];
    return {
      ...criteria,
      dismissCount: (criteria.dismissCount ?? 0) + 1,
      lastDismissDate: today,
    };
  }

  if (isCompositeCriteria(criteria)) {
    let hasUpdate = false;
    const updatedConditions = criteria.conditions.map((condition) => {
      const updated = incrementDismissCount(condition);
      if (updated) {
        hasUpdate = true;
        return updated;
      }
      return condition;
    });
    return hasUpdate ? { ...criteria, conditions: updatedConditions } : undefined;
  }

  return undefined;
};
